import {
  hasAnyPermission as hasAnyPermissionIn,
  hasPopulatedPermissionMap,
} from '../../assets/utils/permissions';
import { ORG_MANAGE_PLANS_PERMISSIONS, canManageOrgPlans } from './planRequestHelpers';

/**
 * Per-action gates for the Organizations screen.
 *
 * The screen gated the PAGE and then showed every action to whoever got in:
 * Edit organization, Renew policy, Disable/Enable and Set password rendered
 * unconditionally, with only Manage plans checked. A Client Success Manager
 * holding corporations.read + corporations.view_reports saw the full admin
 * toolbar — which is what "they can do everything on orgs" described.
 *
 * Each list below mirrors the permissions the corresponding endpoint already
 * checks, so the UI stops promising what the API will refuse:
 *
 *   Edit / Disable  -> $orgProfilePerms, PATCH /v2/corporations/{socialNo}
 *                      (and LegacyAdminActionGate::ACTION_UPDATE_ORG_DATA)
 *   Renew           -> LegacyAdminActionGate::ACTION_UPDATE_ORG_EXPIRY —
 *                      organizations.manage only, deliberately narrower than
 *                      a single policy's expiry because it cascades org-wide
 *   Set password    -> PUT /v2/corporations/admin/{hash}/password
 *   Share rosters   -> LegacyAdminActionGate::ACTION_DOWNLOAD_ORG_USERS_DATA
 *   Manage plans    -> ORG_MANAGE_PLANS_PERMISSIONS (already gated)
 *
 * This is defence in depth and honesty about what a role can do — it is NOT
 * the access control. The server lists above are. Note too that a client-side
 * gate only binds when the server's RBAC_ENFORCEMENT_MODE is `enforce`; in
 * any other mode the endpoints admit everyone regardless of what is rendered.
 */

export const ORG_EDIT_PERMISSIONS = [
  'admin.*',
  'corporations.manage',
  'organizations.manage',
];

/** Enable/disable an organisation — same endpoint as Edit (org_status patch). */
export const ORG_STATUS_PERMISSIONS = ORG_EDIT_PERMISSIONS;

/**
 * Renewing coverage cascades to every client and enrollee under the
 * organisation, so `clients.update` is deliberately NOT enough here — the
 * server makes the same distinction.
 */
export const ORG_RENEW_PERMISSIONS = [
  'admin.*',
  'organizations.manage',
];

export const ORG_SET_PASSWORD_PERMISSIONS = [
  'admin.*',
  'admin.user_management',
  'corporations.manage',
  'organizations.manage',
];

/**
 * Exporting or emailing an organisation's roster.
 *
 * corporations.view_reports genuinely grants this — the export endpoint
 * accepts it — so a reports-only role can email out full enrollee PII. That is
 * the server's rule, mirrored rather than tightened here; narrowing it is a
 * policy decision, not a UI one.
 */
export const ORG_SHARE_ROSTER_PERMISSIONS = [
  'admin.*',
  'enrollees.read_all',
  'corporations.view_reports',
];

/** Legacy super-user / account-management role ids, honoured only with no RBAC map. */
export const LEGACY_ORG_ADMIN_ROLES = [1, 5];

/**
 * Permission-first check with the same legacy fallback shape used across the
 * orgs feature: a stale staff role id counts ONLY while the session carries no
 * RBAC permission map at all. A populated map always decides alone.
 */
export function canDoOrgAction(permissions, user, permissionSource, legacyRoles = LEGACY_ORG_ADMIN_ROLES) {
  const granted = typeof permissionSource === 'function'
    ? Boolean(permissionSource(permissions))
    : hasAnyPermissionIn(permissions, permissionSource);
  if (granted) return true;
  const permissionMap = typeof permissionSource === 'function' ? null : permissionSource;
  if (hasPopulatedPermissionMap(permissionMap, user)) return false;
  return legacyRoles.includes(Number(user?.role?.staffRoleId));
}

/**
 * Every org action gate in one object, for a component to spread into props.
 *
 * `canAdministerOrg` is true when at least one action is available — the
 * toolbar itself is hidden when nothing in it can be used, rather than
 * rendering an empty menu.
 */
export function resolveOrgActionAccess(user, permissionSource) {
  const canEdit = canDoOrgAction(ORG_EDIT_PERMISSIONS, user, permissionSource);
  const canSetStatus = canDoOrgAction(ORG_STATUS_PERMISSIONS, user, permissionSource);
  const canRenew = canDoOrgAction(ORG_RENEW_PERMISSIONS, user, permissionSource);
  const canSetPassword = canDoOrgAction(ORG_SET_PASSWORD_PERMISSIONS, user, permissionSource);
  const canShareRosters = canDoOrgAction(ORG_SHARE_ROSTER_PERMISSIONS, user, permissionSource);
  const canManagePlans = canManageOrgPlans(user, permissionSource);

  return {
    canEdit,
    canSetStatus,
    canRenew,
    canSetPassword,
    canShareRosters,
    canManagePlans,
    canAdministerOrg: canEdit || canSetStatus || canRenew || canSetPassword || canManagePlans,
  };
}

export { ORG_MANAGE_PLANS_PERMISSIONS };
